import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import type { ProductStatus } from "@nh/shared";
import { Button, Input, Select } from "@/shared/ui/core";
import { FilterBar } from "@/features/admin/components";
import { useProductStatusOptions } from "./ProductStatusBadge";
import type { ProductFilters, ProductSort } from "../filters";

export interface ProductsToolbarProps {
  filters: ProductFilters;
  /** Las categorías que existen HOY en el catálogo, según el API. */
  categories: readonly string[];
  onChange: (patch: Partial<ProductFilters>) => void;
  onReset: () => void;
  /** `true` cuando hay algo que limpiar. */
  canReset: boolean;
}

const sortLabels: Record<ProductSort, string> = {
  recommended: "Orden del catálogo",
  newest: "Más recientes",
  "price-asc": "Precio: de menor a mayor",
  "price-desc": "Precio: de mayor a menor",
};

const SORTS = Object.keys(sortLabels) as ProductSort[];

/**
 * Buscar, filtrar y ordenar el catálogo.
 *
 * Los tres filtros y el orden viajan al API, no se aplican en memoria:
 * el panel tiene que seguir sirviendo con mil productos, y filtrar en el
 * navegador exige traérselos todos primero.
 *
 * El buscador es `type="search"` con un botón de limpiar propio: el que
 * pinta el navegador en ese tipo de campo aparece en unos y no en otros,
 * y no dispara un evento que se pueda escuchar igual en todos.
 */
export function ProductsToolbar({
  filters,
  categories,
  onChange,
  onReset,
  canReset,
}: ProductsToolbarProps) {
  const { t } = useTranslation();
  const statusOptions = useProductStatusOptions();

  return (
    <FilterBar
      actions={
        canReset ? (
          <Button variant="ghost" size="sm" leftIcon={<X />} onClick={onReset}>
            {t("Limpiar filtros")}
          </Button>
        ) : undefined
      }
    >
      <Input
        type="search"
        label={t("Buscar")}
        placeholder={t("Nombre o slug…")}
        icon={<Search />}
        value={filters.search}
        onChange={(event) => onChange({ search: event.target.value })}
        fieldClassName="min-w-[220px] flex-1"
      />

      <Select
        label={t("Estado")}
        placeholder={t("Todos los estados")}
        value={filters.status}
        options={statusOptions}
        onChange={(event) =>
          onChange({ status: event.target.value as ProductStatus | "" })
        }
        fieldClassName="min-w-[170px]"
      />

      <Select
        label={t("Categoría")}
        placeholder={t("Todas las categorías")}
        value={filters.category}
        options={categories.map((category) => ({ value: category, label: category }))}
        onChange={(event) => onChange({ category: event.target.value })}
        fieldClassName="min-w-[190px]"
      />

      <Select
        label={t("Ordenar por")}
        value={filters.sort}
        options={SORTS.map((sort) => ({ value: sort, label: t(sortLabels[sort]) }))}
        onChange={(event) => onChange({ sort: event.target.value as ProductSort })}
        fieldClassName="min-w-[190px]"
      />
    </FilterBar>
  );
}
